/**
 * Lifting 2D vector features onto the terrain.
 *
 * The vector worker emits flat geometry because it has no terrain to stand on.
 * This step, run where the terrain meshes live, gives every feature its height
 * by sampling the *drawn* mesh — so a tree sits on the visible surface and a
 * lake sits in its bed rather than hovering above it.
 */

import type { TerrainMeshData } from './terrainmesh.js';
import type { VectorChunkData } from './vectorfeatures.js';
import type { SignSpec } from './pois.js';
import type { PlacedHelipad } from './helipads.js';
import type { EnergySpot } from '../economy/energy.js';
import type { StairPlan } from './stairs.js';
import { groundSteps, newStairMesh, planStair, raisedSteps, roofAtTop, roofLanding, stairCorridors } from './stairs.js';
import { cityGround, carveWater, carveWaterways, CITY_SMOOTH, LASER_CITY_CAP, LASER_CITY_SMOOTH, urbanWeight } from './cityterrain.js';
import { cutTunnelOpenings } from './tunnelcuts.js';
import { tunnelEdgeFloor } from './tunneledge.js';
import { SurfaceIndex } from './surfaceindex.js';
import { RailingIndex } from './railings.js';
import { vertexNormals } from './normals.js';
import type { Colliders } from './colliders.js';
import { buildColliders } from './colliders.js';
import { buildGuardrails } from './guardrails.js';
import { buildOsmLines, WayCrossings } from './osmlines.js';
import type { BuildingPad, CorridorSegment, JunctionPlate, RoadRibbons } from './roadprofile.js';
import { BUILDING_EMBED } from './buildings.js';
import {
  UNDER_ROAD, buildingPads, carveTerrain, edgeSpan, clearRoadOfGround, designRoadProfiles, pressTerrainUnderRoads, refreshTerrainGeometry, roadEmbankments,
} from './roadprofile.js';
import type { TileKey } from '../geo/tilekey.js';
import { sampleMeshHeight } from '../heightsample.js';
import { buildBridges } from './bridges.js';
import { TUNNEL } from './roads.js';
import type { Portal, TunnelVolume } from './tunnelmesh.js';
import { buildBridgeAreas } from './bridgeareas.js';
import { buildQuays, levelQuays, lowerQuayWater, paintQuayWater } from './quays.js';
import { LANDCOVER_SIZE } from './landcover.js';
import { backfillTroughs, buildTroughs, buildTunnelMeshes, buryTunnels, overMouth } from './tunnelmesh.js';
import { biomeWeights } from '../biome/biome.js';
import { blendedTone } from '../biome/palette.js';

/** An sRGB hex as linear RGB. */
function linearRgb(hex: number): [number, number, number] {
  const f = (c: number) => { const v = c / 255; return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return [f((hex >> 16) & 255), f((hex >> 8) & 255), f(hex & 255)];
}
import { atElevation, seaLevelClimate } from '../biome/climate.js';
import { SPECIES, adaptToSeason, adaptToTemperature } from '../biome/flora.js';
import type { VehicleSpecies } from './parking.js';
import { VEHICLE_SIZE } from './parking.js';
import { snowCover } from '../biome/biome.js';
import { onDay, seasonState } from '../sky/season.js';
import { tileBounds } from '../geo/tilekey.js';
import { LANDCOVER, ROAD_STYLE, ROAD_STYLE_LIFT, WATER_KIND, type RoadStyle } from '../sources/shortbread.js';
import type { Footpath } from '../character/footpaths.js';
import { annotateFootpaths, crossingPoints, extractFootpaths } from '../character/footpaths.js';
import type { Lane } from '../traffic/lanes.js';
import { extractLanes } from '../traffic/lanes.js';
import { zebraMarks } from './zebras.js';

export interface PlacedVectorChunk {
  key: TileKey;
  /**
   * The paths people walk and the lanes cars drive, from the placed roads,
   * and per road vertex the zebra crossings painted on them (`zebraMarks`).
   * Made with the placing, off the main thread.
   */
  footpaths?: Footpath[];
  lanes?: Lane[];
  zebras?: Float32Array;
  /** Tree transforms: x, y, z triples plus per-instance yaw and scale. */
  treePositions: Float32Array;
  treeRotations: Float32Array;
  treeScales: Float32Array;
  /** Species id per point, adjusted for the tree line. See `core/biome/flora.ts`. */
  treeSpecies: Uint8Array;
  /** Paint colour per point, 0 for none. */
  treePaints: Uint32Array;
  /**
   * Forward and sideways slope per point (rise over run), for what must lie
   * on the ground rather than stand up from it: a parked car on a hill. Zero
   * for everything else.
   */
  treeTilts: Float32Array;
  /** Water surface as x, y, z triples, flat per water body. */
  waterPositions: Float32Array;
  waterIndices: Uint32Array;
  /** Road ribbons as x, y, z triples, draped on the terrain. */
  roadPositions: Float32Array;
  roadIndices: Uint32Array;
  /** Per-vertex road shader data: side, distance, style, half-width. */
  roadAttributes: Float32Array;
  /** Per vertex: 1 on a bridge deck, 2 in a tunnel (`BRIDGE`, `TUNNEL` in `roads.ts`). */
  roadBridges: Uint8Array;
  /** Tunnel tubes, unlit: x, y, z triples, linear RGB and indices. See `tunnelmesh.ts`. */
  tunnelPositions?: Float32Array;
  tunnelColors?: Float32Array;
  tunnelIndices?: Uint32Array;
  /** The floor across each tunnel, to stand on and to tell a point is inside one. */
  tunnelFloorPositions?: Float32Array;
  tunnelFloorIndices?: Uint32Array;
  /** The floor raised to the ceiling, same indices: what the cameras stay under. */
  tunnelRoofPositions?: Float32Array;
  /** The floors of the troughs ways through underpasses run in, to stand on. */
  troughFloorPositions?: Float32Array;
  troughFloorIndices?: Uint32Array;
  /** The quays' aprons by the water, and the decks of bridges mapped as areas, to stand on: see `quays.ts`, `bridgeareas.ts`. */
  quayFloorPositions?: Float32Array;
  quayFloorIndices?: Uint32Array;
  /** Portal mouths, where the terrain is not drawn. */
  portals?: Portal[];
  /** Bridge structure — arches, piers, parapets — as x, y, z triples, linear RGB and indices. */
  bridgePositions: Float32Array;
  bridgeColors: Float32Array;
  bridgeIndices: Uint32Array;
  /** Waterway ribbons, draped. */
  waterLinePositions: Float32Array;
  waterLineIndices: Uint32Array;
  /** Pier decks, levelled per pier. */
  pierPositions: Float32Array;
  pierIndices: Uint32Array;
  /** Pitches, courts and pools, draped, with their shader data. */
  surfacePositions: Float32Array;
  surfaceAttributes: Float32Array;
  surfaceIndices: Uint32Array;
  /** Landcover raster, carried through. */
  landcover: Uint8Array;
  landcoverSize: number;
  /** Buildings as x, y, z triples, each grounded on its own footprint. */
  buildingPositions: Float32Array;
  buildingIndices: Uint32Array;
  /**
   * The buildings' roofs, and every other face of them that is flat enough to
   * stand on, as indices into `buildingPositions`: ground to walk on, up there.
   */
  roofIndices: Uint32Array;
  /** Per-vertex RGB bytes in linear light, carried through untouched. */
  buildingColors: Uint8Array;
  buildingGlow: Uint8Array;
  /** Name boards, Y now on the ground under their building. See `pois.ts`. */
  signs: PlacedSign[];
  /** Helipads, each at the height of its surface. See `helipads.ts`. */
  helipads: PlacedHelipad[];
  /** Where fuel and charge are sold, carried through. */
  energy: EnergySpot[];
  /** Police stations, where they are mapped. */
  stations: Array<{ x: number; z: number; name: string }>;
  /** Railway stations and halts, carried through. */
  railStations: Array<{ x: number; z: number; name: string; halt: boolean }>;
  /** Garage doors, carried through. */
  garages: Array<{ x: number; z: number; faceX: number; faceZ: number; span: number }>;
  /**
   * Vertex normals for the smooth-shaded ribbons, computed here — in the
   * placement worker — so the main thread has nothing left to do but wrap
   * buffers. Empty when the part is.
   */
  normals: { roads: Float32Array; water: Float32Array; waterLines: Float32Array; piers: Float32Array; surfaces: Float32Array };
  /**
   * The embankment faces under raised roads, on their own as well as drawn
   * with the bridge structure: they are ground to walk and drive on, so they
   * go into the tile's walkable surfaces too.
   */
  embankmentPositions: Float32Array;
  embankmentIndices: Uint32Array;
  /** Stair treads and landings, to stand on: they are drawn with the bridge structure. */
  stairPositions: Float32Array;
  stairIndices: Uint32Array;
  /** What bodies collide with, in the format of `colliders.ts`. */
  colliders: Colliders;
  /** Highest and lowest point of anything placed, for a bounding sphere. */
  minY: number;
  maxY: number;
  /**
   * True when placing this chunk reshaped its terrain for the roads (see
   * `roadprofile.ts`): the terrain mesh has to be redrawn from its data.
   */
  terrainChanged: boolean;
  /** ENU position of the chunk's north-west corner. */
  originEast: number;
  originNorth: number;
}

/**
 * The height a water body sits at.
 *
 * Taken as a low percentile of the terrain under its outline rather than the
 * minimum: DEM noise and the half-texel edge disagreement mean a single stray
 * low sample would sink the whole lake below its shoreline. A low percentile is
 * robust to a few bad samples while still sitting at the bottom of the basin.
 */
const WATER_PERCENTILE = 0.2;
/**
 * Lift the surface slightly above the sampled level.
 *
 * Counter-intuitive but necessary: a DEM is flat across a lake *at the water
 * surface*, not at the lake bed, so the terrain mesh already sits almost
 * exactly where the water goes. Placing the surface at or below the sampled
 * level buries it inside the terrain and it never appears. A small lift puts it
 * just clear, with the shoreline where the ground rises through it.
 */
const WATER_LIFT = 0.45;

/**
 * How far a built-up shore's water lies under the ground round it, metres:
 * a quay's height over a canal or harbour. Scaled by how built-up the shore
 * is, so a lake in the fields keeps its level.
 */
const QUAY_DROP = 1.8;
/** A river's or lake's bed under its surface, over a tunnel under it, metres. */
const RIVER_BED = 2;
/** How far a river or canal lies under the ground of its banks out of town, metres. */
const RIVER_DROP = 1.2;

/** A per-grid-vertex value at the vertex nearest (x, z). */
/** Under this urban weight a street is out of town, and has guardrails on its sharp bends and banks. */
const RURAL = 0.15;

function gridValue(mesh: TerrainMeshData, values: Float32Array, x: number, z: number): number {
  const j = Math.max(0, Math.min(mesh.gridRes, Math.round(x / mesh.width * mesh.gridRes)));
  const i = Math.max(0, Math.min(mesh.gridRes, Math.round(z / mesh.depth * mesh.gridRes)));
  return values[i * (mesh.gridRes + 1) + j];
}

/**
 * Shore spread, metres, past which a water body is treated as flowing rather
 * than still. A lake's shore samples agree to a metre or so; a river through a
 * town falls several metres across one tile.
 */
const FLOWING_SPREAD = 2;
/** How far along a river the surface looks for its own level, metres. */
const FLOWING_REACH = 70;

/**
 * Surface height per vertex of one water body, from the terrain under its
 * outline (`xz` as x,z pairs, `ground` the sampled heights).
 *
 * Still water is flat: one level for the whole body, a low percentile of its
 * shore. But one level for a river is wrong the moment it runs downhill —
 * levelled at the bottom of its fall, the upstream reaches sit metres under
 * the ground and the river shows as scattered puddles. So a body whose shore
 * falls more than `FLOWING_SPREAD` is levelled locally instead: each vertex
 * takes the same low percentile, but only of the shore within
 * `FLOWING_REACH` of it. The surface then steps down with the valley, still
 * sitting at the bottom of the channel rather than up on the banks.
 */
export function waterSurface(xz: ArrayLike<number>, ground: ArrayLike<number>): Float32Array {
  const count = ground.length;
  const out = new Float32Array(count);
  if (count === 0) return out;
  const sorted = Float64Array.from(ground).sort();
  const pick = (values: Float64Array, n: number) => values[Math.min(n - 1, Math.floor(n * WATER_PERCENTILE))];
  const flat = pick(sorted, count) + WATER_LIFT;
  const spread = sorted[Math.min(count - 1, Math.floor(count * 0.8))] - sorted[Math.floor(count * WATER_PERCENTILE)];
  if (spread <= FLOWING_SPREAD) return out.fill(flat);

  // Bucket vertices on a grid one reach across, so each looks at 3×3 cells.
  const cells = new Map<string, number[]>();
  const key = (x: number, z: number) => `${Math.floor(x / FLOWING_REACH)},${Math.floor(z / FLOWING_REACH)}`;
  for (let i = 0; i < count; i++) {
    const k = key(xz[i * 2], xz[i * 2 + 1]);
    const list = cells.get(k);
    if (list === undefined) cells.set(k, [i]);
    else list.push(i);
  }
  const near = new Float64Array(count);
  const reach2 = FLOWING_REACH * FLOWING_REACH;
  for (let i = 0; i < count; i++) {
    const x = xz[i * 2], z = xz[i * 2 + 1];
    const cx = Math.floor(x / FLOWING_REACH), cz = Math.floor(z / FLOWING_REACH);
    let n = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        for (const j of cells.get(`${cx + dx},${cz + dz}`) ?? []) {
          const ex = xz[j * 2] - x, ez = xz[j * 2 + 1] - z;
          if (ex * ex + ez * ez <= reach2) near[n++] = ground[j];
        }
      }
    }
    const local = near.subarray(0, n).sort();
    out[i] = pick(local, n) + WATER_LIFT;
  }
  return out;
}

/**
 * Lift roads clear of the terrain they are draped on.
 *
 * A road ribbon samples the same surface the terrain is drawn from, so at zero
 * offset the two are coplanar and z-fight into a shimmering mess. This is
 * enough to win the depth test at any view distance the game uses, and small
 * enough that the road does not visibly float at walking height.
 */
const ROAD_LIFT = 0.22;
/** How far along the tile's edge the ground is averaged for a road's pin there on the laser ground model, metres each way. See `atEdge`. */
const LASER_EDGE_SPAN = 12;

/**
 * The terrain grid as it was built, before any road reshaped it. Kept per mesh
 * so reshaping always starts from the land itself: a tile's roads are rebuilt
 * whenever its building tags arrive, and each rebuild must give the same
 * ground rather than dig the cutting deeper.
 */
const pristineHeights = new WeakMap<TerrainMeshData, Float32Array>();

/**
 * The mesh's grid as it was built, remembered the first time it is asked
 * for. Placement in a worker reads it here, on the main thread, and sends it.
 */
export function pristineOf(terrain: TerrainMeshData): Float32Array {
  let pristine = pristineHeights.get(terrain);
  if (pristine === undefined) {
    pristine = Float32Array.from(terrain.gridHeights);
    pristineHeights.set(terrain, pristine);
  }
  return pristine;
}

/** Put a mesh's terrain back as it was built, when its roads go away. */
export function restoreTerrain(terrain: TerrainMeshData): boolean {
  const pristine = pristineHeights.get(terrain);
  if (pristine === undefined) return false;
  terrain.gridHeights.set(pristine);
  refreshTerrainGeometry(terrain);
  pristineHeights.delete(terrain);
  return true;
}

/** Where round a raised barrier's point its deck is looked for, metres. */
const DECK_PROBES: ReadonlyArray<readonly [number, number]> = [[0, 0], [2.5, 0], [-2.5, 0], [0, 2.5], [0, -2.5]];

export function placeVectorChunk(
  data: VectorChunkData,
  terrain: TerrainMeshData,
  /** Day of year, 0-based: dresses the trees for the season. */
  dayOfYear = 172,
): PlacedVectorChunk {
  const placed = placeOnTerrain(data, terrain, dayOfYear);
  // Where people walk and cars drive on the placed roads, and the zebras
  // painted where the two cross: here, in the placing worker, not in the frame
  // the tile is shown, where it was 100 ms of a city tile.
  const footpaths = extractFootpaths(placed.roadPositions, placed.roadIndices, {}, placed.roadAttributes);
  const lanes = extractLanes(placed.roadPositions, placed.roadIndices, placed.roadAttributes);
  annotateFootpaths(footpaths, lanes);
  const zebras = zebraMarks(placed.roadPositions, placed.roadIndices, placed.roadAttributes, crossingPoints(footpaths));
  return { ...placed, footpaths, lanes, zebras };
}

function placeOnTerrain(
  data: VectorChunkData,
  terrain: TerrainMeshData,
  dayOfYear: number,
): PlacedVectorChunk {
  // Simplify city ground before roads and buildings choose their levels. Water
  // levels also come from this baseline, never from a neighbouring road fill.
  const pristine = pristineOf(terrain);
  const urban = urbanWeight(terrain, data.landcover, data.landcoverSize);
  // On the laser ground model (`fromOverlay`) only its small bumps between the
  // blocks: its town is the town as it stands, and flattened as the coarse
  // models' bumps are, a street up a hill stood 3 m over the land, on a bank,
  // and the drives up to the houses on it.
  const laser = terrain.fromOverlay === true;
  const city = cityGround(terrain, pristine, data.landcover, data.landcoverSize, urban,
    laser ? LASER_CITY_SMOOTH : CITY_SMOOTH, laser ? LASER_CITY_CAP : Infinity);
  const land: TerrainMeshData = { ...terrain, gridHeights: city };
  const ribbons = {
    positions: data.roadPositions,
    indices: data.roadIndices,
    attributes: data.roadAttributes,
    bridges: data.roadBridges,
    underpasses: data.roadUnderpasses,
  };
  const landAt = (x: number, z: number) => sampleMeshHeight(land, x, z);
  const waterCount = data.waterPositions.length / 2;
  const waterPositions = new Float32Array(waterCount * 3);
  for (let i = 0; i < waterCount; i++) {
    waterPositions[i * 3] = data.waterPositions[i * 2];
    waterPositions[i * 3 + 2] = data.waterPositions[i * 2 + 1];
  }

  for (let r = 0; r + 1 < data.waterRanges.length; r += 2) {
    const start = data.waterRanges[r];
    const count = data.waterRanges[r + 1];
    if (count === 0) continue;

    const samples = new Float64Array(count);
    for (let i = 0; i < count; i++) {
      const v = start + i;
      samples[i] = sampleMeshHeight(land, data.waterPositions[v * 2], data.waterPositions[v * 2 + 1]);
    }
    const levels = waterSurface(data.waterPositions.subarray(start * 2, (start + count) * 2), samples);
    // A city's canals and harbour lie between quay walls, well under the
    // street: level with it, every bridge over them rose into a hump.
    let built = 0;
    if (urban !== undefined) {
      for (let i = 0; i < count; i++) built += gridValue(terrain, urban, data.waterPositions[(start + i) * 2], data.waterPositions[(start + i) * 2 + 1]);
      built /= count;
    }
    // A river or canal lies in its channel, under its banks, as everywhere
    // in the city: level with the shore, it filled its valley to the brim.
    const drop = Math.max(QUAY_DROP * built, data.waterKinds?.[r / 2] === WATER_KIND.flowing ? RIVER_DROP : 0);
    for (let i = 0; i < count; i++) waterPositions[(start + i) * 3 + 1] = levels[i] - drop;
  }

  const average = edgeGround(landAt, data.width, data.depth);
  const edgeTop = edgeGround((x, z) => sampleMeshHeight(terrain, x, z), data.width, data.depth);
  // Where a designed road crosses the tile's edge: the ground averaged along
  // the edge, or high enough to clear the edge's own terrain under it.
  // A way through an underpass at the ground where it crosses, not averaged:
  // along an edge that runs down the embankment it passes under, the average
  // was the embankment's, and it climbed 3 m into the motorway over it.
  // On the laser ground model, which has the road's own surface in it, only
  // a short way along the edge: a motorway's 144 m ran a slip road's pin up
  // the hill beside Örgrytemotet, 3.5 m over the road at the seam, and the
  // cut there stood up out of the road as a step.
  const laserSpan = terrain.fromOverlay === true ? LASER_EDGE_SPAN : Infinity;
  const atEdge = (x: number, z: number, style: number, halfWidth: number, underpass: boolean) => {
    const lift = roadLift(style);
    return Math.max(average(x, z, underpass ? 0 : Math.min(edgeSpan(style), laserSpan)), edgeTop(x, z, 0, halfWidth) - (lift - Math.min(lift, UNDER_ROAD)));
  };
  // A tunnel's roof stays under the land over it, and under a river's bed.
  let waterIndex = new SurfaceIndex([{ positions: waterPositions, indices: data.waterIndices }]);
  const overTunnel = (x: number, z: number) => Math.min(landAt(x, z), (waterIndex.heightAt(x, z, Infinity) ?? Infinity) - RIVER_BED);
  // Bridges mapped as areas: one level deck each, for every way on it (see `bridgeareas.ts`).
  const bridgeAreas = buildBridgeAreas(data.bridgeAreaPositions, data.bridgeAreaIndices, data.bridgeAreaRanges, landAt,
    (x, z) => waterIndex.heightAt(x, z, Infinity) !== undefined);
  const tunnelAtEdge = tunnelEdgeFloor(landAt, data.width, data.depth);
  const profile = designRoadProfiles(ribbons, landAt, data.width, data.depth, atEdge, overTunnel, terrain.fromOverlay === true, bridgeAreas.deckAt, tunnelAtEdge);
  // Pads under buildings on sloping ground, cut and filled with the roads.
  const pads = buildingPads(data.buildingProbes, data.buildingRanges.length / 2, landAt);
  // Stairs choose their heights on the land as it is, and the ground is cut
  // and filled to meet the flights on it, with the roads' corridors. The
  // roofs they may lead up to are the buildings stood on that land — near
  // enough to tell which flights meet one, and which end of a flight is up.
  let earlyRoofAt: ((x: number, z: number, ceiling: number) => number | undefined) | undefined;
  if ((data.stairRanges?.length ?? 0) > 0) {
    const early = groundBuildings(data, land, pads);
    const earlyRoofs = new SurfaceIndex([{ positions: early, indices: roofFaces(early, data.buildingIndices) }]);
    earlyRoofAt = (x, z, c) => earlyRoofs.heightAt(x, z, c);
  }
  const stairPlans = planStairs(data, landAt, earlyRoofAt, terrain.fromOverlay === true, deckNear(profile.corridors));
  // A flight up to a roof stands against its building on the ground as it
  // is: only a flight out in the open has the ground raised along it.
  const openFlights = stairPlans.filter(p => !p.raised &&
    (earlyRoofAt === undefined || roofAtTop(p, landAt(p.points[0], p.points[1]), earlyRoofAt) === undefined));
  const stairGround = openFlights.flatMap(stairCorridors);
  // Before a big building on a slope, the ground dug down to its floor, and
  // the roads and paths laid on that. See `cutBeforeBuildings`.
  const dug = cutBeforeBuildings(data, land, pads);
  carveTerrain(terrain, dug, [...profile.corridors, ...stairGround], pads.filter((p): p is BuildingPad => p !== undefined), roadLift, urban);
  // The roads' verges fill back toward the road, which would bury the facade
  // again: the dig holds everywhere but under a road itself.
  if (dug !== city && keepDug(terrain, dug, city, profile.corridors)) refreshTerrainGeometry(terrain);
  // The ground as the roads left it, before the water is cut in: the quays' (see `quays.ts`).
  const beforeWater: TerrainMeshData = { ...terrain, gridHeights: Float32Array.from(terrain.gridHeights) };
  // Where the roads run, flat: a road over the water is a bridge, and walks on from the quay.
  const roadsXZ = new Float32Array((data.roadPositions.length / 2) * 3);
  for (let v = 0; v < data.roadPositions.length / 2; v++) { roadsXZ[v * 3] = data.roadPositions[v * 2]; roadsXZ[v * 3 + 2] = data.roadPositions[v * 2 + 1]; }
  const roadsFlat = new SurfaceIndex([{ positions: roadsXZ, indices: data.roadIndices }]);
  const quayInput = {
    positions: waterPositions, indices: data.waterIndices, ranges: data.waterRanges,
    land: (x: number, z: number) => sampleMeshHeight(beforeWater, x, z),
    built: (x: number, z: number) => (urban === undefined ? 0 : gridValue(terrain, urban, x, z)),
    water: (x: number, z: number) => waterIndex.heightAt(x, z, Infinity) !== undefined,
    crossed: (x: number, z: number) => roadsFlat.heightAt(x, z, Infinity) !== undefined || bridgeAreas.deckAt(x, z) !== undefined,
    width: data.width, depth: data.depth,
  };
  // A canal between quays lies well under them, and its walls are built
  // down to it, and its bed cut under it: the water first.
  let quays = buildQuays(quayInput);
  if (lowerQuayWater(waterPositions, data.waterRanges, quays.edges)) {
    waterIndex = new SurfaceIndex([{ positions: waterPositions, indices: data.waterIndices }]);
    quays = buildQuays(quayInput);
  }
  const areasCut = carveWater(terrain, waterPositions, data.waterIndices);
  // A canal mapped as a line too has its channel cut along it, and eased
  // out past its edge onto the quay: cut before the quays are levelled.
  const linesCut = carveWaterways(terrain, data.waterLinePositions, data.waterLineIndices);
  // The ground level with the street up to each quay's wall, and the water
  // there painted for the terrain to be drawn none in it (see `quays.ts`).
  const quaysLevelled = levelQuays(terrain, beforeWater.gridHeights, quays.edges);
  let landcover = data.landcover, landcoverSize = data.landcoverSize;
  if (quays.polygons.size > 0 && landcover.length === 0) {
    landcoverSize = LANDCOVER_SIZE;
    landcover = new Uint8Array(landcoverSize * landcoverSize);
  }
  paintQuayWater(landcover, landcoverSize, data.width, data.depth, waterPositions, data.waterIndices, data.waterRanges, quays.polygons);
  // What stands on a bridge mapped as an area stands on its deck.
  const quayFloor = bridgeAreas.floor.indices.length > 0 ? new SurfaceIndex([bridgeAreas.floor]) : undefined;
  const standAt = (x: number, z: number) => {
    const ground = sampleMeshHeight(terrain, x, z);
    const quay = quayFloor?.heightAt(x, z, Infinity);
    return quay !== undefined && quay > ground ? quay : ground;
  };
  // A tunnel's roof standing out of the land, under a dip it could not get
  // under, is buried rather than left in the open.
  const buried = buryTunnels(terrain, data.roadPositions, data.roadIndices, data.roadAttributes, data.roadBridges, profile.design);
  const waterChanged = linesCut || areasCut || buried || quaysLevelled;
  if (waterChanged) refreshTerrainGeometry(terrain);
  // The roads' final heights, pressing the ground down under them — before
  // anything else is placed, so trees and houses stand on the ground as it
  // ends up.
  // Then cut at the tile's edge: the neighbour draws the rest. See `clipToTile`.
  // The cut is laid at the height both tiles agree on there: an engineered
  // road at the edge ground it was pinned to, anything else on the ground.
  // A tunnel's, under that ground, as the neighbour holds it (see `solveTunnel`).
  const cutHeight = (v: number, x: number, z: number, halfWidth: number) => (Number.isNaN(profile.design[v]) ? sampleMeshHeight(terrain, x, z)
    // A way through an underpass, at what both tiles pin it to there (`underpassEdge`).
    : data.roadUnderpasses?.[v] === 1 ? profile.design[v]
    : data.roadBridges[v] === TUNNEL ? tunnelAtEdge(x, z, data.roadAttributes[v * 4 + 2] ?? 3)
    : atEdge(x, z, data.roadAttributes[v * 4 + 2] ?? 3, halfWidth, false))
    + roadLift(data.roadAttributes[v * 4 + 2] ?? 3);
  const { positions: roadPositions, indices: roadIndices } =
    clipToTile(drapeRoads(data, terrain, profile.design), data.roadIndices, data.roadBridges, data.width, data.depth, cutHeight);
  // The troughs the ways through underpasses run in, walled either side:
  // no bank comes down into one, and the ground behind each wall is filled
  // up to its top, before anything is stood on it.
  const roadOver = roadsOver(roadPositions, roadIndices, data.roadBridges, data.roadUnderpasses);
  const troughs = buildTroughs(roadPositions, roadIndices, data.roadAttributes, data.roadUnderpasses, (x, z) => sampleMeshHeight(terrain, x, z), roadOver,
    { width: data.width, depth: data.depth });
  if (backfillTroughs(terrain, troughs, roadOver)) refreshTerrainGeometry(terrain);

  // The worker chose species from the tile's sea-level climate; here, with
  // real heights, each one is adjusted for the temperature where it stands, so
  // a mountainside thins from forest to snowy spruce to bare rock on its own.
  const bounds = tileBounds(data.key);
  const midLat = (bounds.north + bounds.south) / 2;
  const sea = seaLevelClimate(midLat, (bounds.west + bounds.east) / 2);

  const total = data.treePositions.length / 2;
  const kept: number[] = [];
  const heights: number[] = [];
  const species: number[] = [];
  for (let i = 0; i < total; i++) {
    const h = standAt(data.treePositions[i * 2], data.treePositions[i * 2 + 1]);
    const local = atElevation(sea, h);
    let adapted = adaptToTemperature(data.treeSpecies[i] ?? 0, local.temperature);
    if (adapted < 0) continue;
    // Then for the season, at this tree's own height: autumn reaches the
    // hilltops first, and the snow line comes down the mountain in winter.
    const u = ((Math.imul(i + 1, 2654435761) >>> 0) % 10007) / 10007;
    adapted = adaptToSeason(adapted, seasonState(local, midLat, dayOfYear), snowCover(onDay(local, midLat, dayOfYear)), u);
    kept.push(i);
    heights.push(h);
    species.push(adapted);
  }

  const treeCount = kept.length;
  const treePositions = new Float32Array(treeCount * 3);
  const treeRotations = new Float32Array(treeCount);
  const treeScales = new Float32Array(treeCount);
  const treeSpecies = new Uint8Array(species);
  const treePaints = new Uint32Array(treeCount);
  const treeTilts = new Float32Array(treeCount * 2);
  for (let k = 0; k < treeCount; k++) {
    const i = kept[k];
    treePositions[k * 3] = data.treePositions[i * 2];
    treePositions[k * 3 + 1] = heights[k];
    treePositions[k * 3 + 2] = data.treePositions[i * 2 + 1];
    treeRotations[k] = data.treeRotations[i];
    treeScales[k] = data.treeScales[i];
    treePaints[k] = data.treePaints?.[i] ?? 0;
    // A parked vehicle rests on its wheels: tilted to the ground under
    // them, at the height of the plane they touch.
    const size = VEHICLE_SIZE[SPECIES[treeSpecies[k]] as VehicleSpecies] as (typeof VEHICLE_SIZE)[VehicleSpecies] | undefined;
    if (size !== undefined) {
      const x = treePositions[k * 3], z = treePositions[k * 3 + 2], yaw = treeRotations[k];
      const fx = -Math.sin(yaw), fz = -Math.cos(yaw), rx = Math.cos(yaw), rz = -Math.sin(yaw);
      const l = size.halfLength * 0.8, w = size.halfWidth * 0.9;
      const front = standAt(x + fx * l, z + fz * l);
      const back = standAt(x - fx * l, z - fz * l);
      const right = standAt(x + rx * w, z + rz * w);
      const left = standAt(x - rx * w, z - rz * w);
      treeTilts[k * 2] = (front - back) / (2 * l);
      treeTilts[k * 2 + 1] = (right - left) / (2 * w);
      treePositions[k * 3 + 1] = (front + back + right + left) / 4;
    }
  }

  // Bridges last among the roads: they are raised to clear what they cross,
  // which needs everything under them already draped.
  const waterLinePositions = drape(data.waterLinePositions, terrain, WATERWAY_LIFT);
  // In a mapped water a waterway's line runs under its surface: draped on the
  // ground kept at the street's level over a canal (see `quays.ts`), it lay
  // over the canal at the street's height.
  for (let v = 0; v < waterLinePositions.length / 3; v++) {
    const level = waterIndex.heightAt(waterLinePositions[v * 3], waterLinePositions[v * 3 + 2], Infinity);
    if (level !== undefined) waterLinePositions[v * 3 + 1] = Math.min(waterLinePositions[v * 3 + 1], level - UNDER_SURFACE);
  }
  const naturalBank = linearRgb(blendedTone(biomeWeights(sea), 'ground'));
  const pavedBank: [number, number, number] = [0.32, 0.31, 0.29];
  const bankColour = (x: number, z: number): [number, number, number] => {
    const size = data.landcoverSize;
    // Check a small neighbourhood: road ribbons themselves can cover unpainted
    // map pixels, but their banks should match the surrounding pavement.
    for (const [dx, dz] of [[0, 0], [-8, 0], [8, 0], [0, -8], [0, 8]]) {
      const col = Math.max(0, Math.min(size - 1, Math.floor((x + dx) / data.width * size)));
      const row = Math.max(0, Math.min(size - 1, Math.floor((z + dz) / data.depth * size)));
      const cls = data.landcover[row * size + col];
      if (cls === LANDCOVER.urban || cls === LANDCOVER.paving || cls === LANDCOVER.parking || cls === LANDCOVER.drive || cls === LANDCOVER.concrete) return pavedBank;
    }
    return naturalBank;
  };
  const inTrough = (x: number, z: number) => troughs.boxes.some(b => overMouth(b, x, z, 0.5));
  // OSM's railings on bridges: the decks reach out to them, and the game's own parapets and rails give way.
  const railings = new RailingIndex(data.osmLines);
  const bridges = buildBridges({
    railings,
    inArea: (x, z) => bridgeAreas.deckAt(x, z) !== undefined,
    keepOut: inTrough,
    roadPositions,
    roadIndices,
    // What passes under a deck includes the neighbours' roads past the edge.
    underIndices: data.roadIndices,
    roadAttributes: data.roadAttributes,
    roadBridges: data.roadBridges,
    roadUnderpasses: data.roadUnderpasses,
    waterPositions: waterLinePositions,
    waterIndices: data.waterLineIndices,
    ground: (x, z) => sampleMeshHeight(terrain, x, z),
    width: data.width,
    depth: data.depth,
    waterLevel: (x, z) => waterIndex.heightAt(x, z, Infinity),
    embankment: bankColour,
  });
  // Embankment faces under raised roads, drawn with the bridge structure.
  const clippedFlat = new Float32Array((roadPositions.length / 3) * 2);
  for (let v = 0; v < clippedFlat.length / 2; v++) {
    clippedFlat[v * 2] = roadPositions[v * 3];
    clippedFlat[v * 2 + 1] = roadPositions[v * 3 + 2];
  }
  // Tunnels: the tube under the ground, and the portals into it.
  const tunnels = buildTunnelMeshes(roadPositions, roadIndices, data.roadAttributes, data.roadBridges,
    (x, z) => sampleMeshHeight(terrain, x, z), data.width, data.depth);
  // No bank comes down into a trough, a tube or a portal's mouth.
  const banks = roadEmbankments({ ...ribbons, positions: clippedFlat, indices: roadIndices }, roadPositions,
    (x, z) => sampleMeshHeight(terrain, x, z), bankColour,
    (x, z, bottom, top) => inTrough(x, z) || (!tunnels.volume.empty && tunnels.volume.reaches(x, z, bottom, top, 0.3)));
  const helipads = groundHelipads(data, terrain, pads);
  const buildingPositions = groundBuildings(data, terrain, pads);
  const roofIndices = roofFaces(buildingPositions, data.buildingIndices);
  // Roofs a flight's top may be beside, looked up only when there are flights.
  let roofs: SurfaceIndex | undefined;
  const roofAt = (x: number, z: number, ceiling: number) =>
    (roofs ??= new SurfaceIndex([{ positions: buildingPositions, indices: roofIndices }])).heightAt(x, z, ceiling);
  const stairs = buildStairs(stairPlans, (x, z) => sampleMeshHeight(terrain, x, z), roofAt);
  tunnels.volume.withMouths(troughs.boxes);
  // Guardrails along the motorways, and main roads' sharp bends and banks.
  const rails = buildGuardrails(roadPositions, roadIndices, data.roadAttributes, data.roadBridges, data.roadUnderpasses,
    (x, z) => sampleMeshHeight(terrain, x, z), (x, z) => urban === undefined || gridValue(terrain, urban, x, z) < RURAL, railings,
    (x, z) => troughs.boxes.some(b => overMouth(b, x, z, 1)), (x, z) => bridgeAreas.deckAt(x, z) !== undefined);
  // OSM's power lines, fences, walls, hedges and masts, on the ground as it now is.
  // A barrier on a bridge stands on its deck: the highest road surface within
  // a few metres that is well above the ground (a railing is at the deck's
  // edge, a little past the ribbon), else the ground.
  // The roads' surfaces and the decks widened out to these railings.
  const decks = new SurfaceIndex([{ positions: roadPositions, indices: roadIndices }, { positions: bridges.widened.positions, indices: bridges.widened.indices }]);
  const deckAt = (x: number, z: number): number | undefined => {
    const g = sampleMeshHeight(terrain, x, z);
    let best: number | undefined;
    for (const [dx, dz] of DECK_PROBES) {
      const h = decks.heightAt(x + dx, z + dz, g + 15);
      if (h !== undefined && h > g + 1.5 && (best === undefined || h > best)) best = h;
    }
    return best;
  };
  // Barriers open where a road or a path runs through them at their level (see `WayCrossings`).
  const osmLines = buildOsmLines(data.osmLines, data.masts, data.width, data.depth, (x, z) => sampleMeshHeight(terrain, x, z), deckAt,
    new WayCrossings(data.roadPositions, data.roadIndices, roadPositions));
  // Nothing that stands on the land reaches into a tunnel or its portals:
  // not a tree over a portal, its foot where the terrain is not drawn, nor
  // a house over a shallow tunnel.
  const trees = clearOfTunnels(tunnels.volume, treePositions, treeScales);
  const clearedBuildings = clearBuildingsOfTunnels(tunnels.volume, data, buildingPositions);
  const visibleBuildings = cutTunnelOpenings(buildingPositions, data.buildingColors, data.buildingGlow, data.buildingIndices, tunnels.volume);
  const walkBanks = appendMesh(banks, bridges.widened);
  const structure = appendMesh(appendMesh(appendMesh(appendMesh(bridges, banks), stairs), junctionPlates([...profile.junctions, ...cornerPlates(roadPositions, roadIndices, data.roadAttributes, data.roadBridges)])), appendMesh(appendMesh(appendMesh(appendMesh(appendMesh(tunnels.portals, troughs.mesh), rails.mesh), osmLines.mesh), quays.mesh), bridgeAreas.mesh));
  const pierPositions = levelPiers(data, terrain);
  const surfacePositions = drape(data.surfacePositions, terrain, SURFACE_LIFT);
  let minY = terrain.minHeight, maxY = terrain.maxHeight;
  for (let i = 1; i < buildingPositions.length; i += 3) if (buildingPositions[i] > maxY) maxY = buildingPositions[i];
  for (let i = 1; i < structure.positions.length; i += 3) if (structure.positions[i] > maxY) maxY = structure.positions[i];
  if (treePositions.length > 0) maxY += 30; // the tallest tree over its root
  for (let i = 1; i < roadPositions.length; i += 3) if (roadPositions[i] < minY) minY = roadPositions[i];

  // City smoothing, water channels and pads can reshape tiles without roads.
  const reshaped = city !== pristine || dug !== city || waterChanged || troughs.backs.length > 0 || profile.corridors.length > 0 || pads.some(p => p !== undefined);
  return {
    key: data.key,
    treePositions: pick(treePositions, trees, 3),
    treeRotations: pick(treeRotations, trees, 1),
    treeScales: pick(treeScales, trees, 1),
    treeSpecies: pick(treeSpecies, trees, 1),
    treePaints: pick(treePaints, trees, 1),
    treeTilts: pick(treeTilts, trees, 2),
    waterPositions,
    waterIndices: data.waterIndices,
    roadPositions,
    roadIndices,
    roadAttributes: data.roadAttributes,
    roadBridges: data.roadBridges,
    tunnelPositions: tunnels.inside.positions,
    tunnelColors: tunnels.inside.colors,
    tunnelIndices: tunnels.inside.indices,
    tunnelFloorPositions: tunnels.floor.positions,
    tunnelFloorIndices: tunnels.floor.indices,
    tunnelRoofPositions: tunnels.floor.roof,
    troughFloorPositions: troughs.floor.positions,
    troughFloorIndices: troughs.floor.indices,
    quayFloorPositions: bridgeAreas.floor.positions,
    quayFloorIndices: bridgeAreas.floor.indices,
    portals: [...tunnels.mouths, ...troughs.boxes],
    bridgePositions: structure.positions,
    bridgeColors: structure.colors,
    bridgeIndices: structure.indices,
    waterLinePositions,
    waterLineIndices: data.waterLineIndices,
    pierPositions,
    pierIndices: data.pierIndices,
    surfacePositions,
    surfaceAttributes: data.surfaceAttributes,
    surfaceIndices: data.surfaceIndices,
    landcover,
    landcoverSize,
    buildingPositions: visibleBuildings.positions,
    buildingIndices: visibleBuildings.indices,
    roofIndices: visibleBuildings.positions === buildingPositions ? roofIndices : roofFaces(visibleBuildings.positions, visibleBuildings.indices),
    buildingColors: visibleBuildings.colors,
    buildingGlow: visibleBuildings.glow,
    signs: groundSigns(data, terrain, pads),
    helipads,
    energy: data.energy ?? [],
    stations: data.stations ?? [],
    railStations: data.railStations ?? [],
    garages: data.garages ?? [],
    normals: {
      roads: vertexNormals(roadPositions, roadIndices),
      water: vertexNormals(waterPositions, data.waterIndices),
      waterLines: vertexNormals(waterLinePositions, data.waterLineIndices),
      piers: vertexNormals(pierPositions, data.pierIndices),
      surfaces: vertexNormals(surfacePositions, data.surfaceIndices),
    },
    colliders: buildColliders({
      probes: clearedBuildings.probes, ranges: data.buildingRanges, positions: buildingPositions,
      solidCount: data.buildingSolidCount ?? data.buildingRanges.length / 2,
      outlines: data.buildingOutlines, outlineRanges: clearedBuildings.outlineRanges,
      carriageway: carriagewayPoints(data),
      treePositions: pick(treePositions, trees, 3), treeScales: pick(treeScales, trees, 1),
      treeSpecies: pick(treeSpecies, trees, 1), treeRotations: pick(treeRotations, trees, 1),
      walls: concat(concat(concat(concat(concat(tunnels.walls, troughs.walls), rails.walls), bridges.walls), osmLines.walls), concat(quays.walls, bridgeAreas.walls)),
    }),
    // The widened decks too: drawn with the bridges, walked on with these.
    embankmentPositions: walkBanks.positions,
    embankmentIndices: walkBanks.indices,
    stairPositions: stairs.walkPositions,
    stairIndices: stairs.walkIndices,
    minY,
    maxY,
    terrainChanged: reshaped,
    originEast: terrain.originEast,
    originNorth: terrain.originNorth,
  };
}

/**
 * The ground a road leaving the tile is pinned to: averaged along the tile
 * edge nearest the point, over 48 m. Both tiles hold that edge row, so both
 * pin the road to the same height, and one lump at the crossing no longer
 * becomes a bump in the road.
 */
function edgeGround(ground: (x: number, z: number) => number, width: number, depth: number) {
  return (x: number, z: number, span: number, halfWidth = 0): number => {
    const cx = Math.min(width, Math.max(0, x)), cz = Math.min(depth, Math.max(0, z));
    // Which edge: the one the point is beyond, or else the nearest.
    const toEdge = [cz, depth - cz, cx, width - cx];
    const beyond = [z < 0, z > depth, x < 0, x > width];
    let edge = beyond.indexOf(true);
    if (edge < 0) edge = toEdge.indexOf(Math.min(...toEdge));
    let sum = 0, n = 0;
    for (let o = -span; o <= span; o += 6) {
      const px = edge >= 2 ? (edge === 2 ? 0 : width) : Math.min(width, Math.max(0, cx + o));
      const pz = edge < 2 ? (edge === 0 ? 0 : depth) : Math.min(depth, Math.max(0, cz + o));
      sum += ground(px, pz);
      n++;
    }
    // And never under the ground the road spans on the edge itself: the
    // edge's terrain vertices are shared with the neighbour, so nothing
    // presses them down, and a road laid under them was lifted over them
    // after — a hump on the last few metres to every seam.
    let top = -Infinity;
    for (let o = -halfWidth; o <= halfWidth + 1e-6; o += Math.max(0.5, halfWidth / 4)) {
      const px = edge >= 2 ? (edge === 2 ? 0 : width) : Math.min(width, Math.max(0, cx + o));
      const pz = edge < 2 ? (edge === 0 ? 0 : depth) : Math.min(depth, Math.max(0, cz + o));
      top = Math.max(top, ground(px, pz));
    }
    return halfWidth > 0 ? Math.max(sum / n, top) : sum / n;
  };
}

/**
 * The surface of the road or deck over chunk-local (x, z), if one is higher
 * than `above`: what a trough's walls, and the ground filled behind them,
 * stay under. Not tunnels, not the ways through the troughs themselves.
 */
function roadsOver(positions: Float32Array, indices: Uint32Array, bridges: Uint8Array, underpasses: Uint8Array | undefined):
  (x: number, z: number, above: number) => number | undefined {
  if (underpasses === undefined || !underpasses.includes(1)) return () => undefined;
  const kept: number[] = [];
  for (let q = 0; q * 6 < indices.length; q++) {
    const a = indices[q * 6];
    if (bridges[a] === TUNNEL || underpasses[a] === 1) continue;
    for (let k = 0; k < 6; k++) kept.push(indices[q * 6 + k]);
  }
  const index = new SurfaceIndex([{ positions, indices: new Uint32Array(kept) }]);
  return (x, z, above) => {
    const h = index.heightAt(x, z, above + 40);
    return h !== undefined && h > above ? h : undefined;
  };
}

function concat(a: Float32Array, b: Float32Array): Float32Array {
  const out = new Float32Array(a.length + b.length);
  out.set(a);
  out.set(b, a.length);
  return out;
}

/** How tall a tree or prop is reckoned to stand over its foot, at scale 1, metres, for keeping it out of a tunnel. */
const STANDING = 22;

/** Which trees and props to keep: those that reach into no tunnel or portal. Undefined to keep all. */
function clearOfTunnels(volume: TunnelVolume, positions: Float32Array, scales: Float32Array): number[] | undefined {
  if (volume.empty) return undefined;
  const keep: number[] = [];
  for (let i = 0; i < scales.length; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2], scale = scales[i] || 1;
    if (!volume.reaches(x, z, y - 1, y + STANDING * scale, 1.5 * scale)) keep.push(i);
  }
  return keep.length === scales.length ? undefined : keep;
}

/** The kept entries of a per-instance array, `stride` values each. */
function pick<T extends Float32Array | Uint8Array | Uint32Array>(values: T, keep: number[] | undefined, stride: number): T {
  if (keep === undefined) return values;
  const out = new (values.constructor as new (n: number) => T)(keep.length * stride);
  keep.forEach((i, k) => { for (let j = 0; j < stride; j++) out[k * stride + j] = values[i * stride + j]; });
  return out;
}

/**
 * Take out every building reaching into a tunnel or a portal: its vertices
 * folded onto one point, so it draws nothing, and no box or outline left to
 * collide with. Returns the probes and outline ranges for the colliders.
 *
 * Only where the tunnel takes a real share of it (`TUNNEL_SHARE` of its
 * footprint): a house on a portal goes, but a shopping centre over its
 * delivery tunnels (Nordstan, 290 by 370 m) stands, the tunnels under it.
 */
/** Of a building's footprint, the share a tunnel or portal may take before the building goes. */
const TUNNEL_SHARE = 0.2;

/** The share of a footprint (a box: its four corners in order) over a tunnel or a portal, sampled 10 x 10. */
function tunnelShare(volume: TunnelVolume, box: Array<[number, number]>, bottom: number, top: number): number {
  const [a, b, , d] = box;
  let hit = 0;
  for (let i = 0; i < 10; i++) {
    for (let j = 0; j < 10; j++) {
      const u = (i + 0.5) / 10, v = (j + 0.5) / 10;
      if (volume.reaches(a[0] + u * (b[0] - a[0]) + v * (d[0] - a[0]), a[1] + u * (b[1] - a[1]) + v * (d[1] - a[1]), bottom, top, 0)) hit++;
    }
  }
  return hit / 100;
}

function clearBuildingsOfTunnels(volume: TunnelVolume, data: VectorChunkData, positions: Float32Array):
  { probes: Float32Array; outlineRanges: Uint32Array | undefined } {
  if (volume.empty) return { probes: data.buildingProbes, outlineRanges: data.buildingOutlineRanges };
  let probes = data.buildingProbes, outlineRanges = data.buildingOutlineRanges;
  for (let b = 0; b * 2 < data.buildingRanges.length; b++) {
    const start = data.buildingRanges[b * 2], count = data.buildingRanges[b * 2 + 1];
    if (count === 0) continue;
    let bottom = Infinity, top = -Infinity;
    for (let v = start; v < start + count; v++) {
      bottom = Math.min(bottom, positions[v * 3 + 1]);
      top = Math.max(top, positions[v * 3 + 1]);
    }
    // Its footprint: the box the worker probed, else its vertices' bounds.
    let outline: Array<[number, number]>;
    const c = data.buildingProbes.subarray(b * 8, b * 8 + 8);
    if (c.length === 8 && Math.hypot(c[2] - c[0], c[3] - c[1]) > 0.1) {
      outline = [[c[0], c[1]], [c[2], c[3]], [c[4], c[5]], [c[6], c[7]]];
    } else {
      let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
      for (let v = start; v < start + count; v++) {
        minX = Math.min(minX, positions[v * 3]); maxX = Math.max(maxX, positions[v * 3]);
        minZ = Math.min(minZ, positions[v * 3 + 2]); maxZ = Math.max(maxZ, positions[v * 3 + 2]);
      }
      outline = [[minX, minZ], [maxX, minZ], [maxX, maxZ], [minX, maxZ]];
    }
    // Its foundation may reach a little under the ground over a tunnel's roof.
    if (!volume.overlaps(outline, bottom + BUILDING_EMBED, top)) continue;
    if (tunnelShare(volume, outline, bottom + BUILDING_EMBED, top) < TUNNEL_SHARE) continue;
    for (let v = start + 1; v < start + count; v++) positions.copyWithin(v * 3, start * 3, start * 3 + 3);
    if (probes === data.buildingProbes) probes = probes.slice();
    if (b * 8 + 8 <= probes.length) probes.fill(0, b * 8, b * 8 + 8);
    if (outlineRanges !== undefined && b * 2 + 1 < outlineRanges.length) {
      if (outlineRanges === data.buildingOutlineRanges) outlineRanges = outlineRanges.slice();
      outlineRanges[b * 2 + 1] = 0;
    }
  }
  return { probes, outlineRanges };
}

/**
 * Cut the draped road ribbons at the tile's edge.
 *
 * A vector tile carries a strip of its neighbours' roads past its edge, and
 * the ribbons run on through it. So along every tile edge both tiles drew the
 * same stretch of road, each at its own heights. The two copies lay one a
 * little over the other, and the ends of the neighbour's copy stood up out
 * of the road. Here quads wholly outside the tile are dropped, and one that
 * crosses the edge is cut back to it, both kerbs at the same point along
 * the road so the cut is square. Both tiles pin a road's height at their shared
 * edge to the same ground, so the two halves meet there. Bridge decks are
 * left whole: they are solved end to end, not per tile.
 */
export function clipToTile(
  placed: Float32Array, indices: Uint32Array, bridges: Uint8Array, width: number, depth: number,
  /** The height to lay a cut at, for vertex v of a cross-section whose middle is moved to (x, z) on the edge. By default, where the road was. */
  cutHeight?: (v: number, x: number, z: number, halfWidth: number) => number,
): { positions: Float32Array; indices: Uint32Array } {
  const positions = placed.slice();
  const kept: number[] = [];
  const quads = Math.floor(indices.length / 6);
  const inside = (x: number, z: number) => x >= 0 && z >= 0 && x <= width && z <= depth;
  // The middle of the cross-section starting at vertex a (a left, a + 1 right).
  const mid = (a: number): [number, number] =>
    [(placed[a * 3] + placed[(a + 1) * 3]) / 2, (placed[a * 3 + 2] + placed[(a + 1) * 3 + 2]) / 2];
  // How far from p to q the centreline stays in the tile, 0..1.
  const reach = (p: [number, number], q: [number, number]): number => {
    let t = 1;
    const dx = q[0] - p[0], dz = q[1] - p[1];
    if (q[0] > width && dx > 0) t = Math.min(t, (width - p[0]) / dx);
    if (q[0] < 0 && dx < 0) t = Math.min(t, -p[0] / dx);
    if (q[1] > depth && dz > 0) t = Math.min(t, (depth - p[1]) / dz);
    if (q[1] < 0 && dz < 0) t = Math.min(t, -p[1] / dz);
    return Math.max(0, t);
  };
  const quadAt = (q: number) => indices[q * 6];
  // A deck is drawn whole by every tile it reaches into, but not by one whose
  // buffer only holds a piece of it. That piece is solved without what it
  // crosses, and stood metres off the neighbour's deck, through it.
  const deckIn = new Uint8Array(quads);
  for (let q0 = 0; q0 < quads;) {
    if (bridges[quadAt(q0)] !== 1 && bridges[quadAt(q0) + 2] !== 1) { q0++; continue; }
    let q1 = q0;
    while (q1 + 1 < quads && quadAt(q1 + 1) === quadAt(q1) + 2 && (bridges[quadAt(q1 + 1)] === 1 || bridges[quadAt(q1 + 1) + 2] === 1)) q1++;
    let reaches = false;
    for (let q = q0; q <= q1 && !reaches; q++) {
      const p = mid(quadAt(q)), r = mid(quadAt(q) + 2);
      reaches = inside(p[0], p[1]) || inside(r[0], r[1]);
    }
    if (reaches) deckIn.fill(1, q0, q1 + 1);
    q0 = q1 + 1;
  }
  for (let q = 0; q < quads; q++) {
    const a = quadAt(q), c = a + 2;
    const quad = Array.from(indices.subarray(q * 6, q * 6 + 6));
    if (bridges[a] === 1 || bridges[c] === 1) { if (deckIn[q] === 1) kept.push(...quad); continue; }
    const pa = mid(a), pc = mid(c);
    const ina = inside(pa[0], pa[1]), inc = inside(pc[0], pc[1]);
    if (ina && inc) { kept.push(...quad); continue; }
    if (!ina && !inc) continue;
    // Crossing the edge. Move the outside cross-section onto it, unless the
    // road comes straight back in and the quad beyond needs it where it is.
    const [from, to] = ina ? [a, c] : [c, a];
    const beyondQuad = ina ? (q + 1 < quads && quadAt(q + 1) === c ? c + 2 : -1) : (q > 0 && quadAt(q - 1) === a - 2 ? a - 2 : -1);
    if (beyondQuad >= 0) {
      const beyond = mid(beyondQuad);
      if (inside(beyond[0], beyond[1])) { kept.push(...quad); continue; }
    }
    const t = reach(ina ? pa : pc, ina ? pc : pa);
    // Ending right on the edge: nothing of it is inside.
    if (t < 1e-6) continue;
    for (const k of [0, 1]) {
      for (let axis = 0; axis < 3; axis++) {
        positions[(to + k) * 3 + axis] = placed[(from + k) * 3 + axis] + (placed[(to + k) * 3 + axis] - placed[(from + k) * 3 + axis]) * t;
      }
    }
    // Each tile solves its own road up to the edge, so a height blended
    // from its side alone differs a little from the neighbour's: a step
    // across every road. Laid at the height both agree on, the halves meet.
    // One height for the whole cut, taken where the centreline crosses: one
    // for each kerb tilted the road sideways wherever the ground falls away
    // along the edge — two metres across a motorway over a gully.
    if (cutHeight !== undefined) {
      const mx = (positions[to * 3] + positions[(to + 1) * 3]) / 2, mz = (positions[to * 3 + 2] + positions[(to + 1) * 3 + 2]) / 2;
      const halfWidth = Math.hypot(positions[to * 3] - positions[(to + 1) * 3], positions[to * 3 + 2] - positions[(to + 1) * 3 + 2]) / 2;
      for (const k of [0, 1]) positions[(to + k) * 3 + 1] = cutHeight(to + k, mx, mz, halfWidth);
    }
    kept.push(...quad);
  }
  return { positions, indices: new Uint32Array(kept) };
}

/**
 * Points along every carriageway — not footways, not bridges over something
 * else — at each ribbon cross-section's centre and between, for "is this on
 * a road?". About every two metres.
 */
function carriagewayPoints(data: VectorChunkData): Float32Array {
  const out: number[] = [];
  const p = data.roadPositions;
  const quads = Math.floor(data.roadIndices.length / 6);
  for (let q = 0; q < quads; q++) {
    const a = data.roadIndices[q * 6];
    if ((data.roadAttributes[a * 4 + 2] ?? 99) > 4 || data.roadBridges[a] === TUNNEL) continue;
    const x0 = (p[a * 2] + p[(a + 1) * 2]) / 2, z0 = (p[a * 2 + 1] + p[(a + 1) * 2 + 1]) / 2;
    const x1 = (p[(a + 2) * 2] + p[(a + 3) * 2]) / 2, z1 = (p[(a + 2) * 2 + 1] + p[(a + 3) * 2 + 1]) / 2;
    const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, z1 - z0) / 2));
    for (let k = 0; k <= steps; k++) out.push(x0 + ((x1 - x0) * k) / steps, z0 + ((z1 - z0) * k) / steps);
  }
  return new Float32Array(out);
}

/** Two position / colour / index meshes as one. */
/** Asphalt as the road shader draws each class, linear RGB, without its grain. */
const ASPHALT: Record<number, [number, number, number]> = {
  0: [0.088, 0.098, 0.111], 1: [0.114, 0.125, 0.138], 2: [0.138, 0.15, 0.162], 3: [0.178, 0.188, 0.198], 4: [0.219, 0.227, 0.238],
};

/**
 * The plates that fill each levelled junction (see `levelJunctions`): its
 * outline as a fan of triangles on the junction's plane, in the biggest
 * road's asphalt, just under the lowest of the ribbons laid on it, so they
 * draw over it and it shows only in the gaps between their ends.
 */
/**
 * Plates of asphalt where two ends of one road class meet at a sharp V: a
 * one-way road mapped as a loop round an island, its two sides joining at
 * each tip, or a road turning back on itself. The ribbon is split at such a
 * corner and each side ends square (see `buildRoadRibbons`), which left a
 * wedge of ground showing between them at the tip. A disc the road's width
 * under the two ends fills it. Just under the ribbons, as a junction's plate.
 */
function cornerPlates(positions: Float32Array, indices: Uint32Array, attributes: Float32Array, bridges: Uint8Array): JunctionPlate[] {
  type End = { x: number; z: number; dx: number; dz: number; hw: number; style: number; y: number; corners: number[] };
  const ends: End[] = [];
  const quads = Math.floor(indices.length / 6);
  const mid = (a: number) => [(positions[a * 3] + positions[(a + 1) * 3]) / 2, (positions[a * 3 + 2] + positions[(a + 1) * 3 + 2]) / 2];
  const end = (a: number, inward: number) => {
    const style = Math.round(attributes[a * 4 + 2]);
    if (style > ROAD_STYLE.service || bridges[a] !== 0) return;
    const [x, z] = mid(a), [ix, iz] = mid(inward);
    const l = Math.hypot(ix - x, iz - z) || 1;
    ends.push({ x, z, dx: (ix - x) / l, dz: (iz - z) / l, hw: attributes[a * 4 + 3], style,
      y: Math.min(positions[a * 3 + 1], positions[(a + 1) * 3 + 1]),
      corners: [positions[a * 3], positions[a * 3 + 2], positions[(a + 1) * 3], positions[(a + 1) * 3 + 2]] });
  };
  let first = -1, prev = -10;
  for (let q = 0; q <= quads; q++) {
    const a = q < quads ? indices[q * 6] : -1;
    if (first >= 0 && a !== prev + 2) {
      end(first, first + 2);
      end(prev + 2, prev);
      first = -1;
    }
    if (a < 0) break;
    if (first < 0) first = a;
    prev = a;
  }
  const plates: JunctionPlate[] = [];
  const under = ROAD_STYLE_LIFT[ROAD_STYLE.service] - 0.03;
  for (let i = 0; i < ends.length; i++) {
    for (let j = i + 1; j < ends.length; j++) {
      const a = ends[i], b = ends[j];
      // Near enough: at one point, or both cut back to the kerb of a road
      // they end against, each along its own way.
      const r = Math.max(a.hw, b.hw);
      if (a.style !== b.style || Math.abs(a.hw - b.hw) > 0.3 || Math.hypot(a.x - b.x, a.z - b.z) > r * 2.5) continue;
      // Leaving within 70° of each other: a V, not a road running on.
      if (a.dx * b.dx + a.dz * b.dz < 0.34) continue;
      // Round the point they meet at, and across the gap between their ends.
      const pts: Array<[number, number]> = [[a.corners[0], a.corners[1]], [a.corners[2], a.corners[3]], [b.corners[0], b.corners[1]], [b.corners[2], b.corners[3]]];
      const cx = (a.x + b.x) / 2, cz = (a.z + b.z) / 2;
      // And the end of the road they split from, if it is there: wider than
      // the two together, it left a notch outside the split.
      for (const c of ends) {
        if (c === a || c === b || c.style !== a.style || Math.hypot(c.x - cx, c.z - cz) > r * 2.5) continue;
        pts.push([c.corners[0], c.corners[1]], [c.corners[2], c.corners[3]]);
      }
      if (Math.hypot(a.x - b.x, a.z - b.z) < 0.5) {
        for (let k = 0; k < 16; k++) pts.push([cx + Math.cos(k / 16 * Math.PI * 2) * r, cz + Math.sin(k / 16 * Math.PI * 2) * r]);
      }
      plates.push({ outline: hull(pts), cx, cz, h: Math.min(a.y, b.y) - 0.03 - under, gx: 0, gz: 0, style: a.style });
    }
  }
  return plates;
}

/** Convex hull of x, z points (monotone chain), as x, z pairs. */
function hull(points: Array<[number, number]>): number[] {
  const pts = [...points].sort((p, q) => p[0] - q[0] || p[1] - q[1]);
  const cross = (o: [number, number], a: [number, number], b: [number, number]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (list: Array<[number, number]>) => {
    const out: Array<[number, number]> = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop();
      out.push(p);
    }
    return out;
  };
  const lower = half(pts), upper = half([...pts].reverse());
  return [...lower.slice(0, -1), ...upper.slice(0, -1)].flat();
}

function junctionPlates(plates: readonly JunctionPlate[]): { positions: Float32Array; colors: Float32Array; indices: Uint32Array } {
  const positions: number[] = [], colors: number[] = [], indices: number[] = [];
  const under = ROAD_STYLE_LIFT[ROAD_STYLE.service] - 0.03;
  for (const p of plates) {
    const base = positions.length / 3;
    const tone = ASPHALT[p.style] ?? ASPHALT[3];
    const n = p.outline.length / 2;
    for (let i = 0; i < n; i++) {
      const x = p.outline[i * 2], z = p.outline[i * 2 + 1];
      positions.push(x, p.h + p.gx * (x - p.cx) + p.gz * (z - p.cz) + under, z);
      colors.push(...tone);
    }
    for (let i = 1; i + 1 < n; i++) indices.push(base, base + i, base + i + 1);
  }
  return { positions: new Float32Array(positions), colors: new Float32Array(colors), indices: new Uint32Array(indices) };
}

function appendMesh(
  a: { positions: Float32Array; colors: Float32Array; indices: Uint32Array },
  b: { positions: Float32Array; colors: Float32Array; indices: Uint32Array },
): { positions: Float32Array; colors: Float32Array; indices: Uint32Array } {
  if (b.indices.length === 0) return a;
  const positions = new Float32Array(a.positions.length + b.positions.length);
  positions.set(a.positions);
  positions.set(b.positions, a.positions.length);
  const colors = new Float32Array(a.colors.length + b.colors.length);
  colors.set(a.colors);
  colors.set(b.colors, a.colors.length);
  const offset = a.positions.length / 3;
  const indices = new Uint32Array(a.indices.length + b.indices.length);
  indices.set(a.indices);
  for (let i = 0; i < b.indices.length; i++) indices[a.indices.length + i] = b.indices[i] + offset;
  return { positions, colors, indices };
}

/** Waterways sit below the roads that cross them, above the ground. */
const WATERWAY_LIFT = 0.16;
/** A waterway's line in a mapped water, under its surface, metres. */
const UNDER_SURFACE = 0.1;
/** Pitches and pools, just above the painted landcover under them. */
const SURFACE_LIFT = 0.12;
/** A pier deck stands this far above the highest ground it touches. */
const PIER_DECK = 0.7;

/** A road style's drawn surface over its design line or the ground, metres. */
function roadLift(style: number): number {
  return ROAD_STYLE_LIFT[style as RoadStyle] ?? ROAD_LIFT;
}

/** Lift flat x,z pairs onto the terrain surface, plus `lift`. */
function drape(flat: Float32Array, terrain: TerrainMeshData, lift: number): Float32Array {
  const count = flat.length / 2;
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const x = flat[i * 2];
    const z = flat[i * 2 + 1];
    out[i * 3] = x;
    out[i * 3 + 1] = sampleMeshHeight(terrain, x, z) + lift;
    out[i * 3 + 2] = z;
  }
  return out;
}

/**
 * Drape road ribbons, each class at its own lift, and span bridges.
 *
 * A bridge draped like a road would dive into the valley it crosses. Instead
 * each bridge's deck runs in a straight line between the ground at its two
 * ends — found by walking the ribbon's quads, which run in order along each
 * road — and is only ever pushed up, never down, where the ground under it
 * rises above that line.
 */
function drapeRoads(data: VectorChunkData, terrain: TerrainMeshData, design: Float32Array): Float32Array {
  const flat = data.roadPositions;
  const count = flat.length / 2;
  const lift = roadLift;
  // Engineered roads take their designed height, level across; the rest —
  // paths, tracks, steps — lie on the ground, which near an engineered road
  // has already been reshaped to meet it.
  const ribbons = { positions: flat, indices: data.roadIndices, attributes: data.roadAttributes, bridges: data.roadBridges, underpasses: data.roadUnderpasses };
  // NaN marks what is still to follow the ground, for the pressing below.
  const heights = new Float32Array(count).fill(NaN);
  for (let i = 0; i < count; i++) {
    if (!Number.isNaN(design[i])) heights[i] = design[i] + lift(data.roadAttributes[i * 4 + 2] ?? 3);
  }
  // Engineered roads keep their design; the ground gives way under them.
  pressTerrainUnderRoads(terrain, ribbons, heights, lift);
  for (let i = 0; i < count; i++) {
    if (Number.isNaN(design[i])) {
      heights[i] = sampleMeshHeight(terrain, flat[i * 2], flat[i * 2 + 1]) + lift(data.roadAttributes[i * 4 + 2] ?? 3);
    }
  }
  // Only where the ground was not reshaped to the road is it lifted — and
  // not in the cell by the tile's edge, where the edge's vertices hold up a
  // bank the design let stand into the road.
  clearRoadOfGround(ribbons, heights, (x, z) => sampleMeshHeight(terrain, x, z), lift, terrain.width, terrain.depth);
  railsOntoStreets(ribbons, heights);
  const out = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    out[i * 3] = flat[i * 2];
    out[i * 3 + 1] = heights[i];
    out[i * 3 + 2] = flat[i * 2 + 1];
  }
  return out;
}

/** A tram's rails stand this far over the street they are set in, metres. */
const TRAM_OVER_STREET = 0.02;

/**
 * Lay tram rails on top of the street they run along. They follow the ground
 * like a path, and the ground under a street is only mostly a kerb's height
 * under it: where it was pressed lower, bare rails would vanish under the
 * asphalt. So each rail vertex takes the highest road surface there, up to a
 * metre over it (a bridge above is not the street), plus a hair. `heights`
 * is edited in place.
 */
function railsOntoStreets(roads: RoadRibbons, heights: Float32Array): void {
  const quads = Math.floor(roads.indices.length / 6);
  const count = roads.positions.length / 2;
  const isTram = (v: number) => roads.attributes[v * 4 + 2] === ROAD_STYLE.tram;
  let trams = false;
  for (let v = 0; v < count && !trams; v++) trams = isTram(v);
  if (!trams) return;
  const positions = new Float32Array(count * 3);
  for (let v = 0; v < count; v++) {
    positions[v * 3] = roads.positions[v * 2];
    positions[v * 3 + 1] = heights[v];
    positions[v * 3 + 2] = roads.positions[v * 2 + 1];
  }
  const indices: number[] = [];
  for (let q = 0; q < quads; q++) {
    const a = roads.indices[q * 6];
    if (isTram(a) || roads.bridges[a] !== 0) continue;
    for (let k = 0; k < 6; k++) indices.push(roads.indices[q * 6 + k]);
  }
  if (indices.length === 0) return;
  const streets = new SurfaceIndex([{ positions, indices: new Uint32Array(indices) }]);
  for (let v = 0; v < count; v++) {
    if (!isTram(v) || roads.bridges[v] !== 0) continue;
    const street = streets.heightAt(roads.positions[v * 2], roads.positions[v * 2 + 1], heights[v] + 1);
    if (street !== undefined) heights[v] = Math.max(heights[v], street + TRAM_OVER_STREET);
  }
}

/** Level each pier at the highest ground it touches, plus deck height. */
function levelPiers(data: VectorChunkData, terrain: TerrainMeshData): Float32Array {
  const out = drape(data.pierPositions, terrain, 0);
  for (let r = 0; r + 1 < data.pierRanges.length; r += 2) {
    const start = data.pierRanges[r];
    const count = data.pierRanges[r + 1];
    let top = 0;
    for (let i = 0; i < count; i++) top = Math.max(top, out[(start + i) * 3 + 1]);
    for (let i = 0; i < count; i++) out[(start + i) * 3 + 1] = top + PIER_DECK;
  }
  return out;
}

/** A sign with its height resolved: `y` is the board's centre, chunk metres. */
export type PlacedSign = Omit<SignSpec, 'probes'>;

/**
 * Put each sign at its building's base, found exactly as the building's own,
 * so a board stays on its wall; a bus stop's board on the ground it stands on.
 */
function groundSigns(data: VectorChunkData, terrain: TerrainMeshData, pads: ReadonlyArray<BuildingPad | undefined>): PlacedSign[] {
  const onPad = pads.filter((p): p is BuildingPad => p !== undefined);
  return data.signs.map(({ probes, onGround, ...sign }) => {
    if (onGround === true) {
      let lowest = Infinity;
      for (let c = 0; c < 4; c++) lowest = Math.min(lowest, sampleMeshHeight(terrain, probes[c * 2], probes[c * 2 + 1]));
      return { ...sign, y: sign.y + (Number.isFinite(lowest) ? lowest : 0) };
    }
    // A board on a building on a pad goes with its building's level.
    const cx = (probes[0] + probes[2] + probes[4] + probes[6]) / 4, cz = (probes[1] + probes[3] + probes[5] + probes[7]) / 4;
    const pad = onPad.find(p => Math.abs(p.cx - cx) < 0.5 && Math.abs(p.cz - cz) < 0.5);
    const base = buildingBase(probes, pad, terrain);
    return { ...sign, y: sign.y + (Number.isFinite(base) ? base : 0) };
  });
}

/**
 * Settle each pad: on a roof, with its building, found as the building's own
 * base is; on the ground, at the highest of the ground under its rim and
 * middle, so no edge of it is buried on a slope.
 */
function groundHelipads(data: VectorChunkData, terrain: TerrainMeshData, pads: ReadonlyArray<BuildingPad | undefined>): PlacedHelipad[] {
  const onPad = pads.filter((p): p is BuildingPad => p !== undefined);
  return (data.helipads ?? []).map(({ probes, onGround, x, y, z, radius, yaw, name }) => {
    let base: number;
    if (onGround) {
      base = sampleMeshHeight(terrain, x, z);
      for (let c = 0; c < 4; c++) base = Math.max(base, sampleMeshHeight(terrain, probes[c * 2], probes[c * 2 + 1]));
    } else {
      const cx = (probes[0] + probes[2] + probes[4] + probes[6]) / 4, cz = (probes[1] + probes[3] + probes[5] + probes[7]) / 4;
      const pad = onPad.find(p => Math.abs(p.cx - cx) < 0.5 && Math.abs(p.cz - cz) < 0.5);
      base = buildingBase(probes, pad, terrain);
    }
    return { x, z, radius, yaw, name, onGround, y: y + (Number.isFinite(base) ? base : 0) };
  });
}

/** The tile's mapped staircases, planned on the land before it is carved. See `planStair`. */
function planStairs(data: VectorChunkData, land: (x: number, z: number) => number,
  roofAt?: (x: number, z: number, ceiling: number) => number | undefined, trueGround = false,
  deckAt?: (x: number, z: number) => { h: number; past: number } | undefined): StairPlan[] {
  const plans: StairPlan[] = [];
  const ranges = data.stairRanges ?? new Uint32Array(0);
  for (let i = 0; i * 2 < ranges.length; i++) {
    const start = ranges[i * 2], count = ranges[i * 2 + 1];
    if (count < 2) continue;
    const plan = planStair(data.stairPoints.subarray(start * 2, (start + count) * 2), data.stairHalfWidths[i],
      data.stairRise[i], data.stairUp[i], land, roofAt, trueGround, deckAt);
    if (plan !== undefined) plans.push(plan);
  }
  return plans;
}

/** How far from a flight's end a footbridge's deck is looked for, metres past its edge. */
const STAIR_DECK_REACH = 3;

/**
 * The bridge deck beside scene (x, z), within `STAIR_DECK_REACH` of its edge:
 * its height there, and how far past its edge the point is (negative on it).
 */
function deckNear(corridors: readonly CorridorSegment[]): (x: number, z: number) => { h: number; past: number } | undefined {
  const decks = corridors.filter(c => c.bridge === true);
  return (x, z) => {
    let best: { h: number; past: number } | undefined, nearest = STAIR_DECK_REACH;
    for (const c of decks) {
      const dx = c.x1 - c.x0, dz = c.z1 - c.z0, l2 = dx * dx + dz * dz || 1;
      const t = Math.max(0, Math.min(1, ((x - c.x0) * dx + (z - c.z0) * dz) / l2));
      const past = Math.hypot(x - (c.x0 + dx * t), z - (c.z0 + dz * t)) - c.halfWidth;
      if (past < nearest) {
        nearest = past;
        best = { h: c.h0 + (c.h1 - c.h0) * t, past };
      }
    }
    return best;
  };
}

/** The tile's stairs, as planned. */
function buildStairs(plans: readonly StairPlan[], ground: (x: number, z: number) => number,
  roofAt: (x: number, z: number, ceiling: number) => number | undefined) {
  const m = newStairMesh();
  for (const p of plans) {
    if (p.raised) raisedSteps(m, p.points, p.halfWidth, p.y0, p.y1, ground);
    else {
      // Between the ground as carved at its two ends: the terrain's cells are
      // metres wide, so the carve reaches only so far toward the plan, and
      // the paths at the ends lie on the ground as it ends up.
      const n = p.points.length;
      const y0 = ground(p.points[0], p.points[1]) + 0.02;
      // Up to the roof its top is beside, when there is one: the stairs up
      // to a car park's deck or a terrace, which the map leaves unjoined.
      const roof = roofAtTop(p, y0, roofAt);
      const y1 = roof?.y ?? Math.max(y0, ground(p.points[n - 2], p.points[n - 1]) + 0.02);
      groundSteps(m, p.points, p.halfWidth, y0, y1, ground);
      if (roof !== undefined) roofLanding(m, { x: p.points[n - 2], z: p.points[n - 1] }, roof, roof.y, p.halfWidth, ground);
    }
  }
  return {
    positions: new Float32Array(m.positions),
    colors: new Float32Array(m.colors),
    indices: new Uint32Array(m.indices),
    walkPositions: new Float32Array(m.walkPositions),
    walkIndices: new Uint32Array(m.walkIndices),
  };
}

/** Steepest roof that can be stood on: the cosine of its pitch, about 60°. */
const ROOF_STEEPEST = 0.5;

/**
 * The building faces a body can stand on: those whose normal points up, or
 * straight down — the winding is not relied on — within `ROOF_STEEPEST`. A
 * face pointing down is the underside of a slab whose top is found with it,
 * and is only ever stood on from below the ground or the slab.
 */
export function roofFaces(positions: Float32Array, indices: Uint32Array): Uint32Array {
  const out: number[] = [];
  for (let i = 0; i + 2 < indices.length; i += 3) {
    const a = indices[i] * 3, b = indices[i + 1] * 3, c = indices[i + 2] * 3;
    const ux = positions[b] - positions[a], uy = positions[b + 1] - positions[a + 1], uz = positions[b + 2] - positions[a + 2];
    const vx = positions[c] - positions[a], vy = positions[c + 1] - positions[a + 1], vz = positions[c + 2] - positions[a + 2];
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len > 1e-6 && Math.abs(ny) / len >= ROOF_STEEPEST) out.push(indices[i], indices[i + 1], indices[i + 2]);
  }
  return new Uint32Array(out);
}

/**
 * Drop each building onto the ground.
 *
 * The worker emits every building with ground level at its own embed depth, so
 * this only has to find a base height per building and shift it. The base is
 * the *lowest* terrain sample under the footprint: choosing the mean or the
 * centre instead leaves the downhill corner hanging in the air on any slope,
 * which is far more noticeable than the uphill side being slightly buried —
 * and being buried is what the embedded skirt is for.
 *
 * Sampling is per building, not per vertex. A detailed house runs to several
 * hundred vertices and sampling all of them would be a hundred times the work
 * for a base height that differs by centimetres, so the worker emits four
 * footprint corners per building and only those are probed. On a dense tile
 * that is the difference between a frame and a hitch.
 */
/**
 * The lowest the ground comes round a building's walls: its corners and
 * points along each wall between them. The terrain's triangles are not
 * the building's shape, and between corners the ground can dip below them.
 */
function lowestAround(corners: ArrayLike<number>, terrain: TerrainMeshData): number {
  let lowest = Infinity;
  for (let e = 0; e < 4; e++) {
    const ax = corners[e * 2], az = corners[e * 2 + 1];
    const bx = corners[((e + 1) % 4) * 2], bz = corners[((e + 1) % 4) * 2 + 1];
    // Every metre or so along the wall: the ground's triangles are 7 m wide,
    // and a dip between two samples is a gap under the wall.
    const steps = Math.max(3, Math.min(24, Math.ceil(Math.hypot(bx - ax, bz - az))));
    for (let k = 0; k < steps; k++) {
      const t = k / steps;
      lowest = Math.min(lowest, sampleMeshHeight(terrain, ax + (bx - ax) * t, az + (bz - az) * t));
    }
  }
  return lowest;
}

/** How big a building must be, its longer side in metres, to have the ground dug out before it. */
const DIG_MIN_SIDE = 12;
/** Least the ground must stand over a building's floor along a wall to be dug out there, metres. */
const DIG_MIN_DEPTH = 1;
/** Deepest the ground is dug before a wall, metres: past that, the building is built into the hill. */
const DIG_MAX_DEPTH = 6;

/** Cells from the tile's edge over which a dig before a building eases out to nothing. */
const DIG_EDGE = 2;

/**
 * The land with the ground dug out before big buildings on slopes.
 *
 * A building too big for a pad (see `buildingPads`) stands on the lowest
 * ground round its walls, and where the land rises against a wall it is
 * buried there. The elevation is far too coarse to know that in town the
 * ground before such a facade — a car park's, a hospital's — is dug away to
 * its floor. So along every wall the ground stands over the floor, it is cut
 * down to it, and slopes back up to the land a few metres out: the facade
 * stands its full height, and the paths and steps before it run at its foot.
 * Only ever lower; never deeper than `DIG_MAX_DEPTH`.
 */
function cutBeforeBuildings(data: VectorChunkData, land: TerrainMeshData, pads: ReadonlyArray<BuildingPad | undefined>): Float32Array {
  const heights = land.gridHeights;
  const out = Float32Array.from(heights);
  const { gridRes, width, depth } = land;
  const side = gridRes + 1;
  const solid = data.buildingSolidCount ?? data.buildingRanges.length / 2;
  let changed = false;
  for (let b = 0; b < solid; b++) {
    if (pads[b] !== undefined || data.buildingRanges[b * 2 + 1] === 0) continue;
    // The outline: its own where it has one, else its box.
    const count = data.buildingOutlineRanges?.[b * 2 + 1] ?? 0;
    const first = data.buildingOutlineRanges?.[b * 2] ?? 0;
    const ring = count >= 3 ? data.buildingOutlines.subarray(first * 2, (first + count) * 2) : data.buildingProbes.subarray(b * 8, b * 8 + 8);
    let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
    for (let k = 0; k < ring.length; k += 2) {
      x0 = Math.min(x0, ring[k]); x1 = Math.max(x1, ring[k]);
      z0 = Math.min(z0, ring[k + 1]); z1 = Math.max(z1, ring[k + 1]);
    }
    if (Math.max(x1 - x0, z1 - z0) < DIG_MIN_SIDE) continue;
    const floor = lowestAround(data.buildingProbes.subarray(b * 8, b * 8 + 8), land);
    if (!Number.isFinite(floor)) continue;
    // Worth doing only where the land stands well over the floor somewhere along the walls.
    let highest = -Infinity;
    const n = ring.length / 2;
    for (let e = 0; e < n; e++) {
      const ax = ring[e * 2], az = ring[e * 2 + 1], bx = ring[((e + 1) % n) * 2], bz = ring[((e + 1) % n) * 2 + 1];
      const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, bz - az) / 3));
      for (let k = 0; k < steps; k++) highest = Math.max(highest, sampleMeshHeight(land, ax + ((bx - ax) * k) / steps, az + ((bz - az) * k) / steps));
    }
    if (highest - floor < DIG_MIN_DEPTH) continue;
    const band = 2 + 1.5 * Math.min(DIG_MAX_DEPTH, highest - floor);
    const j0 = Math.max(0, Math.floor(((x0 - band) / width) * gridRes)), j1 = Math.min(gridRes, Math.ceil(((x1 + band) / width) * gridRes));
    const i0 = Math.max(0, Math.floor(((z0 - band) / depth) * gridRes)), i1 = Math.min(gridRes, Math.ceil(((z1 + band) / depth) * gridRes));
    for (let i = i0; i <= i1; i++) {
      for (let j = j0; j <= j1; j++) {
        const vi = i * side + j;
        const natural = heights[vi];
        const over = natural - floor;
        if (over <= 0.05) continue;
        const x = (j / gridRes) * width, z = (i / gridRes) * depth;
        const d = distanceOutside(ring, x, z);
        const reach = 2 + 1.5 * Math.min(DIG_MAX_DEPTH, over);
        if (d >= reach) continue;
        // Level at the wall, for a metre out; then back up to the land.
        const t = d <= 1 ? 0 : (d - 1) / (reach - 1);
        // Not at the tile's edge, which the neighbour holds too: a building
        // there is dug for by one tile, or each its own way, and the edge
        // stood 5 m apart. Eased up to it over `DIG_EDGE` cells.
        const w = (1 - t * t * (3 - 2 * t)) * Math.min(1, Math.min(i, j, gridRes - i, gridRes - j) / DIG_EDGE);
        const target = Math.max(floor, natural - DIG_MAX_DEPTH);
        const h = natural + (target - natural) * w;
        if (h < out[vi]) { out[vi] = h; changed = true; }
      }
    }
  }
  return changed ? out : heights;
}

/**
 * Put back the dug ground wherever the carve raised it again, except under a
 * road's surface: a road that stays up stands on its embankment beside it.
 */
function keepDug(mesh: TerrainMeshData, dug: Float32Array, land: Float32Array, corridors: readonly CorridorSegment[]): boolean {
  const { gridRes, gridHeights, width, depth } = mesh;
  let changed = false;
  for (let vi = 0; vi < dug.length; vi++) {
    if (dug[vi] >= land[vi] || gridHeights[vi] <= dug[vi]) continue;
    const i = Math.floor(vi / (gridRes + 1)), j = vi % (gridRes + 1);
    const x = (j / gridRes) * width, z = (i / gridRes) * depth;
    let underRoad = false;
    for (const c of corridors) {
      const dx = c.x1 - c.x0, dz = c.z1 - c.z0;
      const t = Math.max(0, Math.min(1, ((x - c.x0) * dx + (z - c.z0) * dz) / (dx * dx + dz * dz || 1)));
      if (Math.hypot(c.x0 + dx * t - x, c.z0 + dz * t - z) <= c.halfWidth + 0.5) { underRoad = true; break; }
    }
    if (underRoad) continue;
    gridHeights[vi] = dug[vi];
    changed = true;
  }
  return changed;
}

/** How far (x, z) is outside the ring of x,z pairs; 0 inside it. */
function distanceOutside(ring: ArrayLike<number>, x: number, z: number): number {
  const n = ring.length / 2;
  let inside = false, best = Infinity;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const ax = ring[j * 2], az = ring[j * 2 + 1], bx = ring[i * 2], bz = ring[i * 2 + 1];
    if ((bz > z) !== (az > z) && x < ((ax - bx) * (z - bz)) / (az - bz) + bx) inside = !inside;
    const dx = bx - ax, dz = bz - az;
    const t = Math.max(0, Math.min(1, ((x - ax) * dx + (z - az) * dz) / (dx * dx + dz * dz || 1)));
    best = Math.min(best, Math.hypot(ax + dx * t - x, az + dz * t - z));
  }
  return inside ? 0 : best;
}

/**
 * Where a building's base goes. Its vertices start at the foot of its buried
 * skirt, `BUILDING_EMBED` under its floor, and doors and steps stand on the
 * floor. The floor goes where the ground is: the pad's level, or without a
 * pad the lowest ground round the walls, so no door hangs over a drop. And
 * the skirt's foot must be at or below the ground all round, or the house
 * shows in the air: wherever a pad's fill fell deeper short than the skirt
 * reaches — a road cut beside it, a neighbour — the house sinks with it.
 */
export function buildingBase(corners: ArrayLike<number>, pad: BuildingPad | undefined, terrain: TerrainMeshData): number {
  const lowest = lowestAround(corners, terrain);
  return Math.min((pad?.level ?? lowest) - BUILDING_EMBED, lowest);
}

function groundBuildings(data: VectorChunkData, terrain: TerrainMeshData, pads: ReadonlyArray<BuildingPad | undefined>): Float32Array {
  const positions = Float32Array.from(data.buildingPositions);
  const count = data.buildingRanges.length / 2;

  for (let b = 0; b < count; b++) {
    const start = data.buildingRanges[b * 2];
    const vertices = data.buildingRanges[b * 2 + 1];
    if (vertices === 0) continue;

    const lowest = buildingBase(data.buildingProbes.subarray(b * 8, b * 8 + 8), pads[b], terrain);
    if (!Number.isFinite(lowest)) continue;

    for (let i = 0; i < vertices; i++) positions[(start + i) * 3 + 1] += lowest;
  }

  return positions;
}
